import React, { useMemo, useRef, useEffect } from "react";
// If this file lives in src/components and your CSS is in src/pages/styles:
import "../styles/LeftSlideOver.css"; // ← adjust if your path differs

/**
 * LeftSlideOver — reusable left slide-in panel with an icon toolbar
 *
 * Props:
 * - isOpen: boolean
 * - title: string | ReactNode (header title)
 * - onClose: () => void
 * - items: Array<{ key, icon, title?: string, render: () => JSX, disabled?: boolean }>
 * - activeKey: string
 * - onChange: (key) => void
 * - showHeader: boolean (default true)
 */
const LeftSlideOver = ({
  isOpen,
  title,
  onClose,
  items = [],
  activeKey,
  onChange,
  showHeader = true,
}) => {
  const active = useMemo(
    () => items.find((t) => t.key === activeKey) || items[0],
    [items, activeKey],
  );

  const navRef = useRef(null);

  // Close with ESC
  useEffect(() => {
    if (!isOpen) return;
    const onKey = (e) => {
      if (e.key === "Escape") onClose?.();
    };
    const closeSlideOver = () => onClose?.();
    window.addEventListener("keydown", onKey);
    window.addEventListener("close-left-slide-over", closeSlideOver);
    return () => {
      window.removeEventListener("keydown", onKey);
      window.removeEventListener("close-left-slide-over", closeSlideOver);
    };
  }, [isOpen, onClose]);

  // Keyboard navigation within the toolbar
  const focusTabAt = (idx) => {
    const btns = navRef.current?.querySelectorAll("button.lso-iconbtn");
    if (!btns || btns.length === 0) return;
    const clamped = (idx + btns.length) % btns.length;
    btns[clamped].focus();
    const key = btns[clamped].dataset.key;
    if (key) onChange?.(key);
  };

  const handleToolbarKeyDown = (e) => {
    const btns = navRef.current?.querySelectorAll("button.lso-iconbtn");
    if (!btns || btns.length === 0) return;

    const currentIndex = Array.from(btns).findIndex(
      (b) => b === document.activeElement,
    );
    if (e.key === "ArrowRight") {
      e.preventDefault();
      focusTabAt((currentIndex === -1 ? 0 : currentIndex) + 1);
    } else if (e.key === "ArrowLeft") {
      e.preventDefault();
      focusTabAt((currentIndex === -1 ? 0 : currentIndex) - 1);
    } else if (e.key === "Home") {
      e.preventDefault();
      focusTabAt(0);
    } else if (e.key === "End") {
      e.preventDefault();
      focusTabAt(btns.length - 1);
    }
  };

  return (
    <div
      className={`lso-root ${isOpen ? "open" : ""}`}
      aria-hidden={!isOpen}
      onClick={(event) => {
        if (event.target === event.currentTarget) onClose?.();
      }}
    >
      <div className="lso-backdrop" onClick={onClose} />
      <aside
        className="lso-panel"
        role="dialog"
        aria-modal="true"
        aria-label={typeof title === "string" ? title : "Slide panel"}
      >
        {/* Icon toolbar */}
        {items.length > 0 && (
          <nav
            className="lso-toolbar"
            role="tablist"
            aria-label="Slide navigation"
            ref={navRef}
            onKeyDown={handleToolbarKeyDown}
          >
            {items.map((it) => {
              const isActive = (active?.key ?? items[0]?.key) === it.key;
              return (
                <button
                  key={it.key}
                  data-key={it.key}
                  className={`lso-iconbtn ${isActive ? "active" : ""}`}
                  onClick={() => !it.disabled && onChange?.(it.key)}
                  title={it.title || ""}
                  aria-selected={isActive}
                  role="tab"
                  disabled={!!it.disabled}
                >
                  {/* Using whatever you pass—Font Awesome <i>, SVG, or <img> */}
                  <span className="lso-icon">{it.icon}</span>
                </button>
              );
            })}
          </nav>
        )}
        {showHeader && (
          <header className="lso-header">
            <div className="lso-title">
              <span className="lso-title-text">
                {title}
                {active?.title ? ` - ${active.title}` : ""}
              </span>
            </div>
            <button className="btn btn-secondary" onClick={onClose}>
              Close
            </button>
          </header>
        )}

        <div className="lso-content">
          {active?.render ? active.render() : null}
        </div>
      </aside>
    </div>
  );
};

export default LeftSlideOver;
